import { useEffect, useState } from 'react'
import { SubmitRiddleResponse as BLSubmitRiddleResponse } from './../../../utilities/BL/Zones'

const Riddle = ({ riddle, currentZone, roomId }) => {
    const [selectedOption, setSelectedOption] = useState(null)
    const [hasVoted, setHasVoted] = useState(false)
    const [isVotingClosed, setIsVotingClosed] = useState(false)
    const [isItemVisible, setIsItemVisible] = useState(false)

    // Extraer identificadores y propiedades
    const questionId = riddle?.questionId || riddle?.id
    const resolvedRoomId = roomId || (typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('roomId') : null)

    // Helper functions para extraer textos e imágenes de forma segura
    const getOptionText = (opt) => {
        if (!opt) return ''
        if (typeof opt === 'string') return opt
        return opt.text || opt.name || ''
    }

    const getOptionId = (opt) => {
        if (!opt || typeof opt !== 'object') return null
        return opt.id !== undefined ? opt.id : null
    }

    const getResponseText = (resp) => {
        if (!resp) return ''
        if (typeof resp === 'string') return resp
        return resp.text || resp.name || ''
    }

    const getResponseImage = (resp, fallback) => {
        if (resp && typeof resp === 'object' && resp.image) return resp.image
        return fallback || null
    }

    // Sincronización reactiva y persistencia ante F5
    useEffect(() => {
        const closed = riddle?.isVotingOpen === false
        const visible = Boolean(riddle?.isItemVisible)

        setIsVotingClosed(closed)
        setIsItemVisible(visible)

        if (!questionId) {
            setSelectedOption(null)
            setHasVoted(false)
            return
        }

        // Cargar voto previo de sessionStorage para resiliencia ante F5
        const storageKey = `riddle_voted_${questionId}`
        const savedData = sessionStorage.getItem(storageKey) || sessionStorage.getItem('eudnrlanrbaewq')

        if (savedData) {
            try {
                const parsed = JSON.parse(savedData)
                if (parsed && typeof parsed === 'object') {
                    // Si trae questionId, validar que pertenezca a esta pregunta
                    if (parsed.questionId !== undefined && parsed.questionId !== questionId) {
                        setSelectedOption(null)
                        setHasVoted(false)
                    } else {
                        const opt = parsed.option || parsed
                        // Buscar en las opciones actuales si existe
                        const matched = riddle?.options?.find(o => 
                            (o.id !== undefined && opt.id !== undefined && o.id === opt.id) ||
                            (getOptionText(o).trim().toLowerCase() === getOptionText(opt).trim().toLowerCase())
                        )
                        setSelectedOption(matched || opt)
                        setHasVoted(true)
                    }
                } else {
                    setSelectedOption(savedData)
                    setHasVoted(true)
                }
            } catch {
                setSelectedOption(savedData)
                setHasVoted(true)
            }
        } else {
            setSelectedOption(null)
            setHasVoted(false)
        }
    }, [riddle, questionId])

    // Flujo de Votación Anti-Cheat (Un solo toque y validación en servidor)
    const handleOptionClick = async (option) => {
        // Bloqueo inmediato al primer toque o si la votación está cerrada o elemento revelado
        if (hasVoted || selectedOption !== null || isVotingClosed || isItemVisible || riddle?.isVotingOpen === false || riddle?.isItemVisible === true) {
            return
        }

        const optionText = getOptionText(option)
        const qId = questionId || 0

        // Bloqueo táctil inmediato en la UI
        setHasVoted(true)
        setSelectedOption(option)

        // Persistir en sessionStorage indexado por id de pregunta
        try {
            const payloadToStore = {
                questionId: qId,
                option: option
            }
            sessionStorage.setItem(`riddle_voted_${qId}`, JSON.stringify(payloadToStore))
            sessionStorage.setItem('eudnrlanrbaewq', JSON.stringify(option))
        } catch (e) {
            console.warn('No se pudo guardar la selección en sessionStorage:', e)
        }

        // Invocar backend sin comprobación de isCorrect en el cliente (Anti-Cheat)
        try {
            const response = await BLSubmitRiddleResponse(qId, optionText, currentZone, resolvedRoomId)
            if (!response.success) {
                console.warn('❌ [Riddle] Error al registrar voto:', response.message)
            }
        } catch (err) {
            console.error('❌ [Riddle] Error de comunicación al enviar voto:', err)
        }
    }

    if (!riddle || riddle.options === undefined || riddle.options.length === 0) {
        return (
            <div className="Riddle">
                <p className="Riddle__Title">Cargando adivinanza...</p>
            </div>
        )
    }

    const userChoiceText = getOptionText(selectedOption)
    const userChoiceImage = selectedOption && typeof selectedOption === 'object' ? selectedOption.image : null
    const responseText = getResponseText(riddle?.response)
    const revealedImage = getResponseImage(riddle?.response, riddle?.image)
    const isUserCorrect = Boolean(hasVoted && userChoiceText && responseText && userChoiceText.trim().toLowerCase() === responseText.trim().toLowerCase())
    const hasImageOptions = riddle.options.some(opt => opt.image && opt.image !== '')

    // =========================================================================
    // FASE 3: ELEMENTO DESCUBIERTO / REVELACIÓN (isItemVisible === true)
    // =========================================================================
    if (isItemVisible) {
        return (
            <div className="Riddle">
                {hasVoted ? (
                    isUserCorrect ? (
                        <div className="Riddle__Success">
                            <h2 className="Riddle__Title text-success">¡Felicidades, acertaste! 🎉</h2>
                            {revealedImage && (
                                <img src={revealedImage} alt="Respuesta revelada" className="Riddle__Revealed" />
                            )}
                            <p className="Riddle__Text mt-3">
                                <strong>Tu respuesta:</strong> {userChoiceText}
                            </p>
                        </div>
                    ) : (
                        <div className="Riddle__NearMiss">
                            <h2 className="Riddle__Title text-warning">¡Estuviste cerca! La respuesta era: {responseText}</h2>
                            {revealedImage && (
                                <img src={revealedImage} alt="Respuesta revelada" className="Riddle__Revealed" />
                            )}
                            <p className="Riddle__Text mt-3">
                                <strong>Has elegido:</strong> {userChoiceText}
                            </p>
                        </div>
                    )
                ) : (
                    <div className="Riddle__Card">
                        <h2 className="Riddle__Title">La respuesta era: {responseText}</h2>
                        {revealedImage && (
                            <img src={revealedImage} alt="Respuesta revelada" className="Riddle__Revealed" />
                        )}
                        <p className="Riddle__Text text-muted mt-2">No registraste una respuesta a tiempo</p>
                    </div>
                )}
            </div>
        )
    }

    // =========================================================================
    // FASE 2: VOTACIONES CERRADAS / ESPERA DE REVELACIÓN (isVotingClosed === true)
    // =========================================================================
    if (isVotingClosed) {
        return (
            <div className="Riddle">
                {hasVoted ? (
                    <div className="Riddle__Waiting">
                        <h2 className="Riddle__Title">Has elegido: {userChoiceText}</h2>
                        {userChoiceImage ? (
                            <img src={userChoiceImage} alt="Opción elegida" className="Riddle__Image" />
                        ) : (
                            riddle.image && <img src={riddle.image} alt="Silueta" className="Riddle__Silhouette" />
                        )}
                        <p className="Riddle__Subtitle text-muted">Esperando la revelación en pantalla...</p>
                        <div className="spinner-border text-danger mt-2" role="status">
                            <span className="visually-hidden">Cargando...</span>
                        </div>
                    </div>
                ) : (
                    <div className="Riddle__Card">
                        <h2 className="Riddle__Title text-warning">¡Ups! Se acabó el tiempo para elegir una respuesta</h2>
                        {riddle.image && (
                            <img src={riddle.image} alt="Silueta" className="Riddle__Silhouette" />
                        )}
                        <p className="Riddle__Subtitle text-muted mt-3">Votación cerrada. Atento a la pantalla para descubrir la respuesta.</p>
                    </div>
                )}
            </div>
        )
    }

    // =========================================================================
    // FASE 1: VOTACIÓN ABIERTA (!isItemVisible && !isVotingClosed)
    // =========================================================================
    return (
        <div className="Riddle">
            {/* Silueta e Instrucción */}
            {riddle.image && (
                <img src={riddle.image} alt="Silueta adivinanza" className="Riddle__Silhouette" />
            )}

            <p className="Riddle__Title">
                {riddle.instruction ? riddle.instruction : (hasVoted ? `Has elegido: ${userChoiceText}` : 'Selecciona una opción')}
            </p>

            {hasVoted && (
                <div className="Riddle__Waiting mb-3">
                    <p className="Riddle__Title mb-1">Has elegido: <strong>{userChoiceText}</strong></p>
                    <p className="Riddle__Text text-muted small mb-0">¡Tu voto ha sido registrado! Espera el cierre de la votación.</p>
                </div>
            )}

            {/* Opciones con o sin imágenes */}
            {hasImageOptions ? (
                <div className="Riddle__Options">
                    {riddle.options.map((option, index) => {
                        const optText = getOptionText(option)
                        const optId = getOptionId(option)
                        const isSelected = hasVoted && (
                            (optId !== null && getOptionId(selectedOption) !== null && optId === getOptionId(selectedOption)) ||
                            (optText && userChoiceText && optText.trim().toLowerCase() === userChoiceText.trim().toLowerCase())
                        )

                        return (
                            <button
                                key={index}
                                type="button"
                                disabled={hasVoted}
                                className={`Riddle__Options__Buttons Btn__Adsl--RedOutline ${isSelected ? 'Riddle__Options__Buttons--Selected' : ''} ${hasVoted ? 'Riddle__Options__Buttons--Disabled' : ''}`}
                                onClick={() => handleOptionClick(option)}
                            >
                                {option.image && (
                                    <img src={option.image} alt={optText} className="Riddle__Options__Buttons__Image" />
                                )}
                                <span className="Riddle__Options__Buttons__Text">{optText}</span>
                            </button>
                        )
                    })}
                </div>
            ) : (
                <div className="Riddle__Tiles">
                    {riddle.options.map((option, index) => {
                        const optText = getOptionText(option)
                        const optId = getOptionId(option)
                        const isSelected = hasVoted && (
                            (optId !== null && getOptionId(selectedOption) !== null && optId === getOptionId(selectedOption)) ||
                            (optText && userChoiceText && optText.trim().toLowerCase() === userChoiceText.trim().toLowerCase())
                        )

                        return (
                            <button
                                key={index}
                                type="button"
                                disabled={hasVoted}
                                className={`Riddle__Tiles__Buttons Btn__Adsl--RedOutline ${isSelected ? 'Riddle__Tiles__Buttons--Selected' : ''} ${hasVoted ? 'Riddle__Tiles__Buttons--Disabled' : ''}`}
                                onClick={() => handleOptionClick(option)}
                            >
                                <span className="Riddle__Tiles__Buttons__Text">{optText}</span>
                            </button>
                        )
                    })}
                </div>
            )}
        </div>
    )
}

export { Riddle }